/**
 * "What changed" (Beta-1 plan P3-05, Tim C0813 "plant manager landing = my plant, alerts, any
 * status changes"): the last N days of new warnings, new / changed / resolved OE Conditions in
 * scope, summarized from data the dashboard already holds (snapshots + conditions) — no extra
 * Firestore reads. Pure module.
 */
import { CONDITION_STATUS_LABELS } from './trlConstants.js';

const DAY = 86400000;
const QUAD_LABEL = { reliability: 'Reliability', efficiency: 'Efficiency', productivity: 'Productivity', safety: 'Safety' };

export function toMillis(v) {
  if (!v) return null;
  if (typeof v === 'number') return v;
  if (v instanceof Date) return v.getTime();
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
}

/**
 * @returns {{ items: Array<{ kind, at, title, detail, conditionId?, trlId?, quadrant? }>, counts }}
 *   kind: 'warning' | 'new' | 'changed' | 'resolved'
 */
export function summarizeChanges({ snapshots = [], conditions = [], days = 7, now = Date.now() } = {}) {
  const since = now - days * DAY;
  const items = [];

  // Warnings: the first snapshot (newest first) in the window that carries each quadrant warning,
  // but only quadrants that were NOT warning before the window began (a new fire, not an old one).
  const inWindow = snapshots.filter((s) => (toMillis(s.calculatedAt) || 0) >= since);
  const before = snapshots.filter((s) => (toMillis(s.calculatedAt) || 0) < since);
  const priorWarn = new Set();
  const oldest = before[0]; // snapshots are newest first → before[0] is the last one before the window
  (oldest?.warnings || []).forEach((w) => priorWarn.add(w.quadrant));
  const seen = new Set();
  [...inWindow].reverse().forEach((s) => {
    (s.warnings || []).forEach((w) => {
      if (seen.has(w.quadrant) || priorWarn.has(w.quadrant)) return;
      seen.add(w.quadrant);
      items.push({
        kind: 'warning',
        at: toMillis(s.calculatedAt),
        quadrant: w.quadrant,
        title: `${QUAD_LABEL[w.quadrant] || w.quadrant} crossed its threshold`,
        detail: `${Number(w.score).toFixed(1)} > ${w.threshold}`,
      });
    });
  });

  for (const c of conditions) {
    const submitted = toMillis(c.submittedAt) || toMillis(c.createdAt);
    const resolved = toMillis(c.resolvedAt);
    const updated = toMillis(c.updatedAt);
    const label = `${c.conditionId ? `${c.conditionId} · ` : ''}${c.title || 'OE Condition'}`;
    if (c.status === 'resolved' && resolved && resolved >= since) {
      items.push({ kind: 'resolved', at: resolved, title: label, detail: 'Resolved', conditionId: c.conditionId, trlId: c.id });
    } else if (submitted && submitted >= since) {
      items.push({ kind: 'new', at: submitted, title: label, detail: `New — ${CONDITION_STATUS_LABELS[c.status] || c.status || ''}`, conditionId: c.conditionId, trlId: c.id });
    } else if (updated && updated >= since && !['withdrawn', 'cancelled'].includes(c.status)) {
      const last = Array.isArray(c.statusHistory) && c.statusHistory.length ? c.statusHistory[c.statusHistory.length - 1] : null;
      items.push({ kind: 'changed', at: toMillis(last?.at) || updated, title: label, detail: CONDITION_STATUS_LABELS[c.status] || c.status || 'Updated', conditionId: c.conditionId, trlId: c.id });
    }
  }

  items.sort((a, b) => (b.at || 0) - (a.at || 0));
  const counts = { warning: 0, new: 0, changed: 0, resolved: 0 };
  items.forEach((it) => { counts[it.kind] += 1; });
  return { items, counts, days };
}
